import type { TimelineEvent } from "@productlane/api/src/browser"

import { parseDeviceInfoText } from "./device-info-line"

// The widget device string leads with the page URL and closes with the device
// type. Matching on that shape keeps other authorless comments out of the
// device-info slot, where they used to be glued onto a customer message.
const DEVICE_TYPE_SUFFIX = /(?:^|\s·\s)(Desktop|Mobile|Tablet)$/

export function isDeviceInfoCommentEvent(event: TimelineEvent): boolean {
	if (
		event.type !== "comment" ||
		event.comment.userId ||
		event.comment.linearCommentId
	) {
		return false
	}
	const parsed = parseDeviceInfoText(event.comment.content)
	if (!parsed) return false
	return !!parsed.url || DEVICE_TYPE_SUFFIX.test(parsed.text)
}

function getEventCreatedAt(event: TimelineEvent): number {
	switch (event.type) {
		case "email":
			return event.email.createdAt
		case "comment":
			return event.comment.createdAt
		case "threadMessage":
			return event.threadMessage.createdAt
		case "slackMessage":
			return event.slackMessage.createdAt
		case "chatMessage":
			return event.chatMessage.createdAt
		case "live-chat-message":
			return event.liveChatMessage.createdAt
		case "live-chat-closed":
			return event.liveChat.createdAt
		case "activity":
			return event.threadActivity.createdAt
		case "agentActivityBurst":
			return event.burst.firstCreatedAt
		case "routineRun":
			return event.run.createdAt
		case "createdAt":
			return event.thread.createdAt
	}
}

function isInboundCustomerMessage(event: TimelineEvent): boolean {
	switch (event.type) {
		case "live-chat-message":
			return event.liveChatMessage.role === "USER"
		case "chatMessage":
			return event.chatMessage.type === "INBOUND"
		case "slackMessage":
			return event.slackMessage.type === "INBOUND"
		case "email":
			return event.email.type === "INBOUND"
		default:
			return false
	}
}

export function getEventStableId(event: TimelineEvent): string {
	switch (event.type) {
		case "email":
			return `email-${event.email.id}`
		case "comment":
			return `comment-${event.comment.id}`
		case "slackMessage":
			return `slack-${event.slackMessage.id}`
		case "chatMessage":
			return `chat-${event.chatMessage.id}`
		case "threadMessage":
			return `thread-${event.threadMessage.id}`
		case "live-chat-message":
			return `live-chat-${event.liveChatMessage.id}`
		case "live-chat-closed":
			return `live-chat-closed-${event.liveChat.id}`
		case "activity":
			return `activity-${event.threadActivity.id}`
		case "agentActivityBurst":
			return event.burst.id
		case "routineRun":
			return event.run.id
		case "createdAt":
			return `created-${event.thread.id}`
	}
}

export type DeviceInfoPlacement =
	| { text: string; target: "thread-note" }
	| { text: string; targetEventId: string }

export function resolveDeviceInfoPlacement(
	events: TimelineEvent[],
	options: { canAttachToThreadNote: boolean },
): DeviceInfoPlacement | null {
	const deviceEvent = events.find(isDeviceInfoCommentEvent)
	if (!deviceEvent || deviceEvent.type !== "comment") return null

	const parsed = parseDeviceInfoText(deviceEvent.comment.content)
	if (!parsed) return null

	let earliestInbound: TimelineEvent | null = null
	let earliestAt = Number.POSITIVE_INFINITY
	for (const event of events) {
		if (!isInboundCustomerMessage(event)) continue
		const at = getEventCreatedAt(event)
		if (at < earliestAt) {
			earliestAt = at
			earliestInbound = event
		}
	}

	if (earliestInbound) {
		return {
			text: parsed.text,
			targetEventId: getEventStableId(earliestInbound),
		}
	}

	if (options.canAttachToThreadNote) {
		return { text: parsed.text, target: "thread-note" }
	}

	return null
}
